import { JOB_STATUSES, type JobListQuery } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { activeQuickFilter } from '../policies/job-quick-filters';
import { DEADLINE_PRESSURE_LABELS, deadlinePressureLabel } from '../policies/deadline-pressure-labels';
import { QUOTE_VALIDITY_COLORS, quoteValidityLabel } from '../policies/quote-validity-labels';
import { buildHoldColumns } from './build-hold-columns';
import { JobDateRangeFilter } from './JobDateRangeFilter';
import { JOB_STATUS_COLORS, JOB_STATUS_LABELS } from '../../../policies/job-status-labels';
import { jobPath } from '../../../jobs.paths';
import type { JobListItem } from '../../../jobs.api';

export interface FilterOption {
  value: string;
  label: string;
}

interface BuildJobColumnsArgs {
  customerOptions: FilterOption[];
  jobTypeOptions: FilterOption[];
  list: Pick<ListQueryControls<JobListQuery>, 'query' | 'setQuery' | 'sortOrderFor' | 'filteredValueFor'>;
}

const STATUS_FILTERS = JOB_STATUSES.map((status) => ({ text: JOB_STATUS_LABELS[status], value: status }));

function formatDate(date: string | null): string {
  return date ? new Date(date).toLocaleDateString() : '—';
}

function lane(item: JobListItem): string {
  if (!item.originZoneName && !item.destinationZoneName) return '—';
  return `${item.originZoneName ?? '—'} → ${item.destinationZoneName ?? '—'}`;
}

type JobListColumns = TableColumnsType<JobListItem>;
type JobColumnList = Pick<ListQueryControls<JobListQuery>, 'query' | 'setQuery' | 'sortOrderFor' | 'filteredValueFor'>;

/** Job number, customer, job type and lane — the columns identifying the job, none of them date-driven. */
function buildJobIdentityColumns(customerOptions: FilterOption[], jobTypeOptions: FilterOption[], list: JobColumnList): JobListColumns {
  return [
    {
      title: 'Job number',
      dataIndex: 'number',
      key: 'number',
      sorter: true,
      sortOrder: list.sortOrderFor('number'),
      render: (number: string, job) => <Link to={jobPath(job.id)}>{number}</Link>,
    },
    {
      title: 'Customer',
      key: 'customerId',
      filters: customerOptions.map((option) => ({ text: option.label, value: option.value })),
      filteredValue: list.filteredValueFor('customerId'),
      render: (_, job) => job.customerName,
    },
    {
      title: 'Job type',
      key: 'jobTypeCode',
      filters: jobTypeOptions.map((option) => ({ text: option.label, value: option.value })),
      filteredValue: list.filteredValueFor('jobTypeCode'),
      render: (_, job) => job.jobTypeName,
    },
    { title: 'Lane', key: 'lane', render: (_, job) => lane(job) },
  ];
}

/** Every date-range column — split out from `buildJobStatusAndDateColumns` to stay under the per-function line limit. */
function buildJobDateRangeColumns(list: JobColumnList): JobListColumns {
  return [
    {
      title: 'Requested date',
      key: 'requestedDate',
      sorter: true,
      sortOrder: list.sortOrderFor('requestedDate'),
      filtered: Boolean(list.query.requestedDateFrom || list.query.requestedDateTo),
      filterDropdown: ({ close }) => (
        <JobDateRangeFilter
          from={list.query.requestedDateFrom}
          to={list.query.requestedDateTo}
          onApply={(range) => list.setQuery({ requestedDateFrom: range.from, requestedDateTo: range.to })}
          close={close}
        />
      ),
      render: (_, job) => formatDate(job.requestedDate),
    },
    {
      title: 'Deadline',
      key: 'deadline',
      sorter: true,
      sortOrder: list.sortOrderFor('deadline'),
      filtered: Boolean(list.query.deadlineFrom || list.query.deadlineTo),
      filterDropdown: ({ close }) => (
        <JobDateRangeFilter
          from={list.query.deadlineFrom}
          to={list.query.deadlineTo}
          onApply={(range) => list.setQuery({ deadlineFrom: range.from, deadlineTo: range.to })}
          close={close}
        />
      ),
      render: (_, job) => (
        <span className="inline-flex items-center gap-1.5">
          {formatDate(job.customerDeliveryDeadline)}
          {job.deadlinePressure && (
            <Tag color={DEADLINE_PRESSURE_LABELS[job.deadlinePressure].color}>{deadlinePressureLabel(job.deadlinePressure, job.daysUntilDeadline)}</Tag>
          )}
        </span>
      ),
    },
  ];
}

/** Status, quote validity and readiness — split out from `buildJobIdentityColumns` to stay under the per-function line limit. */
function buildJobStatusAndDateColumns(list: JobColumnList): JobListColumns {
  return [
    {
      title: 'Status',
      key: 'status',
      sorter: true,
      sortOrder: list.sortOrderFor('status'),
      filters: STATUS_FILTERS,
      filteredValue: list.filteredValueFor('status'),
      render: (_, job) => (
        <Tag color={JOB_STATUS_COLORS[job.status as keyof typeof JOB_STATUS_COLORS]}>{JOB_STATUS_LABELS[job.status as keyof typeof JOB_STATUS_LABELS]}</Tag>
      ),
    },
    ...buildJobDateRangeColumns(list),
    {
      title: 'Quote validity',
      key: 'quoteValidity',
      render: (_, job) =>
        job.quoteValidUntil && (
          <Tag color={QUOTE_VALIDITY_COLORS[job.quoteExpired ? 'expired' : 'valid']}>
            {quoteValidityLabel(job.quoteExpired, formatDate(job.quoteValidUntil))}
          </Tag>
        ),
    },
    {
      title: 'Readiness',
      key: 'readiness',
      render: (_, job) => <Tag color={job.ready ? 'success' : 'default'}>{job.ready ? 'Ready' : 'Not ready'}</Tag>,
    },
  ];
}

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `jobListQuerySchema`. */
export function buildJobColumns({ customerOptions, jobTypeOptions, list }: BuildJobColumnsArgs): TableColumnsType<JobListItem> {
  const holdColumns = activeQuickFilter(list.query.status) === 'on_hold' ? buildHoldColumns(list) : [];
  return [...buildJobIdentityColumns(customerOptions, jobTypeOptions, list), ...buildJobStatusAndDateColumns(list), ...holdColumns];
}
